// Client-side filtering for sections/fp-character-grid.liquid: search, category tabs, one occasion.
// State lives in the URL hash as #characters?cat=history&occasion=birthday&q=holmes, so links such as
// the hero's occasion chips can open the grid with a filter preselected, and back/forward works.

const HASH = '#characters';

class FpCharacterGrid extends HTMLElement {
  connectedCallback() {
    this.cells = [...this.querySelectorAll('[data-fp-cell]')];
    this.search = this.querySelector('[data-fp-search]');
    this.cats = [...this.querySelectorAll('[data-fp-cat]')];
    this.occasions = [...this.querySelectorAll('[data-fp-occ]')];
    this.count = this.querySelector('[data-fp-count]');
    this.empty = this.querySelector('[data-fp-empty]');
    this.state = { cat: '', occasion: '', q: '' };

    this.cats.forEach((btn) =>
      btn.addEventListener('click', () => this.set({ cat: btn.dataset.fpCat }))
    );
    this.occasions.forEach((btn) =>
      btn.addEventListener('click', () => {
        const occ = btn.dataset.fpOcc;
        this.set({ occasion: this.state.occasion === occ ? '' : occ });
      })
    );
    this.search?.addEventListener('input', () => this.set({ q: this.search.value }));
    this.querySelector('[data-fp-search-form]')?.addEventListener('submit', (e) => e.preventDefault());
    this.querySelector('[data-fp-reset]')?.addEventListener('click', () => {
      this.set({ cat: '', occasion: '', q: '' });
      this.search?.focus();
    });

    // Links anywhere on the page that target the grid (hero chips, CTAs) filter in place.
    this.onLinkClick = (event) => {
      const link = event.target.closest('a[href*="#characters"]');
      if (!link || link.pathname !== location.pathname) return;
      event.preventDefault();
      this.applyHash(link.hash);
      this.writeHash(true);
      this.jumpToGrid();
    };
    this.onHashChange = () => {
      if (location.hash.startsWith(HASH)) this.applyHash(location.hash);
    };
    document.addEventListener('click', this.onLinkClick);
    window.addEventListener('hashchange', this.onHashChange);

    if (location.hash.startsWith(HASH)) {
      this.applyHash(location.hash);
      // The browser can't match "#characters?…" to an id, so scroll ourselves once layout settles.
      if (location.hash.includes('?')) {
        const jump = () => setTimeout(() => this.jumpToGrid('instant'), 0);
        if (document.readyState === 'complete') jump();
        else window.addEventListener('load', jump, { once: true });
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onLinkClick);
    window.removeEventListener('hashchange', this.onHashChange);
  }

  applyHash(hash) {
    const params = new URLSearchParams(hash.split('?')[1] || '');
    this.state = {
      cat: params.get('cat') || '',
      occasion: params.get('occasion') || '',
      q: params.get('q') || '',
    };
    if (this.search) this.search.value = this.state.q;
    this.render();
  }

  set(patch) {
    Object.assign(this.state, patch);
    this.render();
    this.writeHash(false);
  }

  writeHash(push) {
    const params = new URLSearchParams();
    if (this.state.cat) params.set('cat', this.state.cat);
    if (this.state.occasion) params.set('occasion', this.state.occasion);
    if (this.state.q.trim()) params.set('q', this.state.q.trim());
    const query = params.toString();
    const url = query ? `${HASH}?${query}` : location.hash.startsWith(HASH) ? HASH : location.pathname + location.search;
    history[push ? 'pushState' : 'replaceState'](null, '', url);
  }

  jumpToGrid(behavior) {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.closest('section')?.scrollIntoView({ behavior: behavior || (reduce ? 'instant' : 'smooth'), block: 'start' });
  }

  render() {
    const { cat, occasion } = this.state;
    const terms = this.state.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let shown = 0;

    for (const cell of this.cells) {
      const visible =
        (!cat || (cell.dataset.categories || `|${cell.dataset.category}|`).includes(`|${cat}|`)) &&
        (!occasion || cell.dataset.occasions.includes(`|${occasion}|`)) &&
        terms.every((t) => cell.dataset.search.includes(t));
      cell.hidden = !visible;
      if (visible) shown++;
    }

    this.cats.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.fpCat === cat)));
    this.occasions.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.fpOcc === occasion)));
    if (this.count) this.count.textContent = `${shown} ${shown === 1 ? 'character' : 'characters'} shown.`;
    if (this.empty) this.empty.hidden = shown > 0;
  }
}

if (!customElements.get('fp-character-grid')) customElements.define('fp-character-grid', FpCharacterGrid);
